import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  openPreferences,
  runSketch,
  startAccessibleSketch,
  startSketch,
  stopSketch,
  setRemoteEditNotice,
  openManageJoinCodeModal
} from '../../actions/ide';
import {
  setAutorefresh,
  setGridOutput,
  setTextOutput
} from '../../actions/preferences';
import PlayIcon from '../../../../images/play.svg';
import StopIcon from '../../../../images/stop.svg';
import PreferencesIcon from '../../../../images/preferences.svg';
import ProjectName from './ProjectName';
import VersionIndicator from '../VersionIndicator';
import { VisibilityDropdown } from '../../../User/components/VisibilityDropdown';
import { changeVisibility } from '../../actions/project';
import ARButton from './ARButton';
import useArSession from './useArSession';
import { getIsStaff, getCurrentClassroomName } from '../../selectors/users';

const Toolbar = (props) => {
  const { isPlaying, infiniteLoop, preferencesIsVisible } = useSelector(
    (state) => state.ide
  );
  const project = useSelector((state) => state.project);
  const user = useSelector((state) => state.user);
  const autorefresh = useSelector((state) => state.preferences.autorefresh);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const arSession = useArSession();
  const isStaff = useSelector(getIsStaff);
  const currentClassroomName = useSelector(getCurrentClassroomName);
  const remoteEditNotice = useSelector((state) => state.ide.remoteEditNotice);
  const userIsOwner =
    user?.authenticated && user?.username === project.owner?.username;
  const showVisibilityDropdown = project?.owner && userIsOwner;
  // The AR button also shows on a brand-new, unsaved sketch (no owner yet): the logged-in user is
  // the prospective owner. Clicking it opens the modal, which reminds them to save the sketch first
  // (ensureArSessionId throws "Save your sketch before opening it in AR." when there's no project id).
  // Staff get it on a student's sketch too — they already edit those in place, and testing in AR is
  // part of that. They publish to a session of their OWN (the student's is owner-locked by the
  // arSessions rule); see ensureArSessionId in client/firebase/arSession.js.
  const showArButton =
    user?.authenticated && (!project?.owner || userIsOwner || isStaff);
  // Staff edit student sketches in place (autosave writes back to the student's project), so make it
  // clear whose sketch this is to avoid an instructor confusing it with their own.
  const showStaffEditingBanner = project?.owner && !userIsOwner && isStaff;

  // Live sync: an instructor's edit was just applied to the student's open sketch. Show a transient
  // banner to the owner, then auto-dismiss it.
  useEffect(() => {
    let timer;
    if (remoteEditNotice) {
      timer = setTimeout(() => dispatch(setRemoteEditNotice(false)), 6000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [remoteEditNotice, dispatch]);

  const playButtonClass = classNames({
    'toolbar__play-button': true,
    'toolbar__play-button--selected': isPlaying
  });
  const stopButtonClass = classNames({
    'toolbar__stop-button': true,
    'toolbar__stop-button--selected': !isPlaying
  });
  const preferencesButtonClass = classNames({
    'toolbar__preferences-button': true,
    'toolbar__preferences-button--selected': preferencesIsVisible
  });

  const handleVisibilityChange = useCallback(
    (sketchId, sketchName, newVisibility) => {
      dispatch(changeVisibility(sketchId, sketchName, newVisibility, t));
    },
    [changeVisibility]
  );

  return (
    <div className="toolbar">
      <button
        className="toolbar__play-sketch-button"
        onClick={() => {
          props.syncFileContent();
          dispatch(startAccessibleSketch());
          dispatch(setTextOutput(true));
          dispatch(setGridOutput(true));
        }}
        aria-label={t('Toolbar.PlaySketchARIA')}
        disabled={infiniteLoop}
      >
        <PlayIcon focusable="false" aria-hidden="true" />
      </button>
      <button
        className={playButtonClass}
        id="play-sketch"
        onClick={() => {
          props.syncFileContent();
          dispatch(runSketch());
        }}
        aria-label={t('Toolbar.PlayOnlyVisualSketchARIA')}
        title={t('Toolbar.PlaySketchARIA')}
        disabled={infiniteLoop}
      >
        <PlayIcon focusable="false" aria-hidden="true" />
      </button>
      <button
        className={stopButtonClass}
        onClick={() => dispatch(stopSketch())}
        aria-label={t('Toolbar.StopSketchARIA')}
        title={t('Toolbar.StopSketchARIA')}
      >
        <StopIcon focusable="false" aria-hidden="true" />
      </button>

      {/* Sits with Run/Stop rather than over on the right: pressing Run is what pushes code to
          AR, so the two controls are one workflow and belong within the same glance. */}
      {showArButton && <ARButton {...arSession} />}

      <div className="toolbar__autorefresh">
        <input
          id="autorefresh"
          className="checkbox__autorefresh"
          type="checkbox"
          checked={autorefresh}
          onChange={(event) => {
            dispatch(setAutorefresh(event.target.checked));
            if (event.target.checked) {
              dispatch(startSketch());
            }
          }}
        />
        <label htmlFor="autorefresh" className="toolbar__autorefresh-label">
          {t('Toolbar.Auto-refresh')}
        </label>
      </div>

      <div className="toolbar__project-name-container">
        <ProjectName />
        {/* Staff editing a student's sketch in place: flag whose project this is. */}
        {showStaffEditingBanner && (
          <p className="toolbar__staff-editing-banner">
            {t('Toolbar.EditingStudentProject', {
              owner: project.owner.username
            })}
          </p>
        )}
        {/* Live sync: the student's owner-facing "your instructor just edited this" banner. */}
        {remoteEditNotice && userIsOwner && (
          <p className="toolbar__remote-edit-notice" role="status">
            {t('Toolbar.InstructorUpdatedSketch')}
          </p>
        )}
        {/* Still show owner if not you */}
        {project?.owner && !userIsOwner && (
          <p className="toolbar__project-owner">
            {t('Toolbar.By')}{' '}
            <Link to={`/${project.owner.username}/sketches`}>
              {project.owner.username}
            </Link>
          </p>
        )}
      </div>

      <div style={{ flex: 1 }} />

      {/* Current classroom (any user type). Denormalized name from the user doc; hidden when none.
          Sits on the right, next to the visibility (public/private) control. Staff can click it to
          manage the join code; for students it's a plain label. */}
      {currentClassroomName &&
        (isStaff ? (
          <button
            type="button"
            className="toolbar__classroom toolbar__classroom--button"
            title={t('Toolbar.ManageJoinCode', {
              defaultValue: 'Manage join code'
            })}
            onClick={() => dispatch(openManageJoinCodeModal())}
          >
            {currentClassroomName}
          </button>
        ) : (
          <p
            className="toolbar__classroom"
            title={t('Toolbar.CurrentClassroom', {
              defaultValue: 'Current classroom'
            })}
          >
            {currentClassroomName}
          </p>
        ))}

      {showVisibilityDropdown && (
        <div className="toolbar__visibility">
          <VisibilityDropdown
            sketch={project}
            onVisibilityChange={handleVisibilityChange}
            location="toolbar"
          />
        </div>
      )}

      <VersionIndicator />

      <button
        className={preferencesButtonClass}
        onClick={() => dispatch(openPreferences())}
        aria-label={t('Toolbar.OpenPreferencesARIA')}
        title={t('Toolbar.OpenPreferencesARIA')}
      >
        <PreferencesIcon focusable="false" aria-hidden="true" />
      </button>
    </div>
  );
};

Toolbar.propTypes = {
  syncFileContent: PropTypes.func.isRequired
};

export default Toolbar;
